import { useTranslation } from "react-i18next";
import { cn } from "@cieda/djayma-ui";
import {
  ORDER_STATUS_COLORS,
  ORDER_STATUS_I18N_KEYS,
  type OrderStatus,
} from "@/lib/admin-api/orders";

// ---------------------------------------------------------------------------
// Status color lookup tables
// ---------------------------------------------------------------------------
//
// Order colors come from the single source of truth in admin-api/orders.ts
// (keyed by the DB English values). Other variants (payment/delivery/return)
// still use French keys because their upstream models have not been migrated.
//

const PAYMENT_STATUS_COLORS: Readonly<Record<string, string>> = {
  en_attente: "bg-amber-100 text-amber-800 border-amber-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  payé: "bg-emerald-100 text-emerald-800 border-emerald-200",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  échoué: "bg-red-100 text-red-800 border-red-200",
  failed: "bg-red-100 text-red-800 border-red-200",
  remboursé: "bg-orange-100 text-orange-800 border-orange-200",
  refunded: "bg-orange-100 text-orange-800 border-orange-200",
};

const DELIVERY_STATUS_COLORS: Readonly<Record<string, string>> = {
  en_attente: "bg-amber-100 text-amber-800 border-amber-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  en_cours: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-blue-100 text-blue-800 border-blue-200",
  livrée: "bg-emerald-100 text-emerald-800 border-emerald-200",
  delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
  échouée: "bg-red-100 text-red-800 border-red-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

const RETURN_STATUS_COLORS: Readonly<Record<string, string>> = {
  demandé: "bg-amber-100 text-amber-800 border-amber-200",
  requested: "bg-amber-100 text-amber-800 border-amber-200",
  approuvé: "bg-blue-100 text-blue-800 border-blue-200",
  approved: "bg-blue-100 text-blue-800 border-blue-200",
  traité: "bg-emerald-100 text-emerald-800 border-emerald-200",
  processed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  refusé: "bg-red-100 text-red-800 border-red-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

// Widen ORDER_STATUS_COLORS (Record<OrderStatus, string>) to the lookup shape
// used by the switch below, without losing the compile-time exhaustiveness
// guarantee at its definition site.
const ORDER_STATUS_COLORS_LOOKUP: Readonly<Record<string, string>> = ORDER_STATUS_COLORS;

const VARIANT_MAP: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  order: ORDER_STATUS_COLORS_LOOKUP,
  payment: PAYMENT_STATUS_COLORS,
  delivery: DELIVERY_STATUS_COLORS,
  return: RETURN_STATUS_COLORS,
};

const DEFAULT_COLORS = "bg-stone-100 text-stone-700 border-stone-200";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StatusBadgeVariant = "order" | "payment" | "delivery" | "return";

export interface StatusBadgeProps {
  readonly status: string;
  readonly variant?: StatusBadgeVariant;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Fallback label: replace underscores with spaces and capitalize first letter. */
function humanizeStatus(status: string): string {
  return status.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function isKnownOrderStatus(status: string): status is OrderStatus {
  return status in ORDER_STATUS_I18N_KEYS;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StatusBadge({ status, variant = "order" }: StatusBadgeProps) {
  const { t } = useTranslation("admin");

  const lookup = VARIANT_MAP[variant] ?? ORDER_STATUS_COLORS_LOOKUP;
  const colors = lookup[status] ?? DEFAULT_COLORS;

  // Order variant uses i18n keys; other variants fall back to humanized strings.
  const displayLabel =
    variant === "order" && isKnownOrderStatus(status)
      ? t(ORDER_STATUS_I18N_KEYS[status], humanizeStatus(status))
      : humanizeStatus(status);

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        colors,
      )}
      data-status={status}
    >
      {displayLabel}
    </span>
  );
}
